import React from "react";
import { useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../assets/theme";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import PageHeader from "../components/PageHeader";
import {
  fetchPromos,
  createPromo,
  deletePromo,
  updatePromo,
  PromoType,
} from "../services/PromoCodeService";

const PromoCode: React.FC = () => {
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );

  const [search, setSearch] = React.useState<string>("");
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const normalizeDateForDisplay = (value: any) => {
    if (!value) return "";
    const match = String(value).match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : String(value);
  };

  const formFields: Field[] = [
    { name: "code", label: "Code", required: true },
    {
      name: "type",
      label: "Type",
      type: "dropdown",
      options: [
        { label: "Percent", value: "PERCENT" },
        { label: "Fixed", value: "FIXED" },
      ],
    },
    { name: "value", label: "Value", type: "number", required: true },
    { name: "max_usage", label: "Max Usage", type: "number" },
    { name: "max_usage_per_client", label: "Max Usage Per Client", type: "number" },
    { name: "start_date", label: "Start Date", type: "date" },
    { name: "expiry", label: "Expiry Date", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "ACTIVE", value: true as any },
        { label: "INACTIVE", value: false as any },
      ],
    },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPromos();
      
      setRows(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error("Failed to fetch promo codes", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch promo codes",
        { position: "top-right" }
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const fetchFunc = useCallback(
    async (params?: any) => {
      let content = [...rows];

      const q = (params && params.search) || "";
      const normalizedQuery = String(q || "").toLowerCase().trim();

      if (normalizedQuery) {
        content = content.filter((r: any) => {
          const usedCount = Array.isArray(r.usages) ? r.usages.length : 0;

          const haystack = [
            r.code,
            r.type,
            r.value,
            r.max_usage,
            r.max_usage_per_client,
            usedCount,
            r.start_date,
            r.expiry,
            r.active === true ? "ACTIVE" : "INACTIVE",
            r.id,
          ]
            .filter((v) => v !== undefined && v !== null && v !== "")
            .map((v: any) => String(v).toLowerCase())
            .join(" ");

          return haystack.includes(normalizedQuery);
        });
      }

      const pageSize = (params && params.size) || 10;

      return {
        content,
        number: 0,
        size: pageSize,
        totalElements: content.length,
        totalPages: Math.max(1, Math.ceil(content.length / pageSize)),
      };
    },
    [rows]
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Promo Codes"
          subtitle="Create and manage discount codes."
        />

        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />

        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "code", header: "Code" },
            { key: "type", header: "Type" },
            { key: "value", header: "Value" },
            {
              key: "usages",
              header: "Usage Count",
              transform: (row: any) =>
                Array.isArray(row.usages) ? row.usages.length : 0,
            },
            { key: "max_usage", header: "Max Usage" },
            { key: "max_usage_per_client", header: "Max Usage / Client" },
            {
              key: "start_date",
              header: "Start Date",
              transform: (row: any) => normalizeDateForDisplay(row.start_date),
            },
            {
              key: "expiry",
              header: "Expiry Date",
              transform: (row: any) => normalizeDateForDisplay(row.expiry),
            },
            {
              key: "status",
              header: "Status",
              transform: (row: any) => (row.active === true ? "ACTIVE" : "INACTIVE"),
            },
          ]}
          actions={wiredActions}
          fetchFunc={fetchFunc}
        />

        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Promo Code` : "Add Promo Code"}
          fields={formFields}
          initialValues={
            editingRow
              ? {
                  code: editingRow.code ?? "",
                  type: (editingRow.type as PromoType) ?? "PERCENT",
                  value:
                    editingRow.value !== undefined && editingRow.value !== null
                      ? String(editingRow.value)
                      : "",
                  max_usage:
                    editingRow.max_usage !== undefined &&
                    editingRow.max_usage !== null
                      ? String(editingRow.max_usage)
                      : "",
                  max_usage_per_client:
                    editingRow.max_usage_per_client !== undefined &&
                    editingRow.max_usage_per_client !== null
                      ? String(editingRow.max_usage_per_client)
                      : "",
                  start_date: normalizeDateForDisplay(editingRow.start_date) || "",
                  expiry: normalizeDateForDisplay(editingRow.expiry) || "",
                  status: editingRow.active === false ? false : true,
                }
              : { type: "PERCENT", status: true }
          }
          onSubmit={async (values) => {
            const payload: any = {
              code: String(values.code || "").trim(),
              type: values.type as PromoType,
              value: Number(values.value),
              max_usage:
                values.max_usage !== undefined &&
                values.max_usage !== null &&
                String(values.max_usage) !== ""
                  ? Math.floor(Number(values.max_usage))
                  : undefined,
              max_usage_per_client:
                values.max_usage_per_client !== undefined &&
                values.max_usage_per_client !== null &&
                String(values.max_usage_per_client) !== ""
                  ? Math.floor(Number(values.max_usage_per_client))
                  : undefined,
              start_date:
                values.start_date !== undefined &&
                values.start_date !== null &&
                String(values.start_date).trim() !== ""
                  ? String(values.start_date).trim()
                  : undefined,
              expiry:
                values.expiry !== undefined &&
                values.expiry !== null &&
                String(values.expiry).trim() !== ""
                  ? String(values.expiry).trim()
                  : undefined,
              active:
                values.status !== undefined && values.status !== null
                  ? Boolean(values.status)
                  : true,
            };

            if (!payload.code || payload.code.length < 3) {
              toast.error("Code must be at least 3 characters.", {
                position: "top-right",
              });
              return;
            }
            if (!payload.type || !["PERCENT", "FIXED"].includes(payload.type)) {
              toast.error("Type must be PERCENT or FIXED.", {
                position: "top-right",
              });
              return;
            }
            if (!Number.isFinite(payload.value) || payload.value < 1) {
              toast.error("Value must be a number and at least 1.", {
                position: "top-right",
              });
              return;
            }
            if (
              payload.max_usage !== undefined &&
              (!Number.isFinite(payload.max_usage) || payload.max_usage < 1)
            ) {
              toast.error("Max Usage must be an integer and at least 1.", {
                position: "top-right",
              });
              return;
            }
            if (
              payload.max_usage_per_client !== undefined &&
              (!Number.isFinite(payload.max_usage_per_client) ||
                payload.max_usage_per_client < 1)
            ) {
              toast.error("Max Usage Per Client must be an integer and at least 1.", {
                position: "top-right",
              });
              return;
            }

            try {
              if (editingRow) {
                await updatePromo(String(editingRow.id), payload);
                toast.success("Promo updated successfully!", {
                  position: "top-right",
                });
              } else {
                await createPromo(payload);
                toast.success("Promo created successfully!", {
                  position: "top-right",
                });
              }
              setEditOpen(false);
              setEditingRow(null);
              await refreshData();
            } catch (error: any) {
              console.error("Failed to save promo", error);
              toast.error(
                error?.response?.data?.message ||
                  error?.message ||
                  "Failed to save promo",
                { position: "top-right" }
              );
            }
          }}
        />

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deletePromo(String(toDeleteId));
              setRows((prev) =>
                prev.filter((r: any) => String(r.id) !== String(toDeleteId))
              );
              toast.success("Promo deleted successfully!", {
                position: "top-right",
              });
            } catch (error: any) {
              console.error("Failed to delete promo", error);
              toast.error(
                error?.response?.data?.message ||
                  error?.message ||
                  "Failed to delete promo",
                { position: "top-right" }
              );
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default PromoCode;
